import type { Stack } from "../../shared/stack.js";
import type { AppContext } from "../Page.js";
import { h } from "../dom.js";
import { stackName } from "../format.js";

/** Stars or unstars a stack, showing the change before the server confirms it. */
export async function toggleFavorite(ctx: AppContext, key: string, stack: Stack): Promise<void> {
  const before = [...ctx.live.favorites];
  const on = !ctx.live.favorites.has(key);
  ctx.live.setFavorites(on ? [...before, key] : before.filter((k) => k !== key));
  try {
    if (on) await ctx.api.favorite(stack);
    else await ctx.api.unfavorite(key);
  } catch (err) {
    ctx.live.setFavorites(before);
    ctx.toast(`Could not ${on ? "star" : "unstar"} ${stackName(stack)}: ${(err as Error).message}`, "error");
  }
}

/** The star that pins a stack to the top of every list. Clicks don't reach the card or row under it. */
export function favoriteStar(ctx: AppContext, key: string, stack: Stack): HTMLButtonElement {
  const on = ctx.live.favorites.has(key);
  return h(
    "button",
    {
      type: "button",
      class: `star${on ? " on" : ""}`,
      title: on ? "Favorite: pinned to the top and watched live. Click to unpin." : "Pin to the top of every list and watch it live",
      "aria-label": on ? `Unstar ${stackName(stack)}` : `Star ${stackName(stack)}`,
      "aria-pressed": String(on),
      onclick: (e: Event) => {
        e.stopPropagation();
        void toggleFavorite(ctx, key, stack);
      },
      onkeydown: (e: KeyboardEvent) => e.stopPropagation(),
    },
    on ? "★" : "☆",
  );
}

/** Favorites first, keeping the order of everything else (Array.prototype.sort is stable). */
export function favoritesFirst<T>(items: T[], isFavorite: (item: T) => boolean): T[] {
  return [...items].sort((a, b) => Number(isFavorite(b)) - Number(isFavorite(a)));
}
